
import React, { useState, useEffect } from 'react';
import { useAuth } from 'oidc-react';
import SidebarTemplate from './templates/Sidebar/SidebarTemplate';
import NavigationRailTemplate from './templates/NavigationRail/NavigationRailTemplate';
import SettingsTemplate from './templates/Settings/SettingsTemplate';
import PlansTemplate from './templates/Plans/PlansTemplate';
import AuthTemplate from './templates/Auth/AuthTemplate';
import CommandPalette from './components/CommandPalette';
import OnboardingTour from './components/Onboarding/OnboardingTour';
import ModernDialog from './components/ModernDialog';
import Toast, { ToastType } from './components/Toast';
import { Conversation, Message, AppConfig, Attachment, AppMode, User, IntegrationFlow, ServiceDefinition, AgentProfile, Plan, IntegrationApp, UserRole } from './types';
import { TRANSLATIONS } from './constants';
import { generateResponse, generateAudio } from './services/geminiService';
import { audioService } from './services/audioService';
import AppHeader from './components/App/AppHeader';
import AppContent from './components/App/AppContent';
import { 
  AuthService, UserService, ConfigService, ChatService, 
  FlowsService, AgentsService, ServicesService, IntegrationsService, PlansService, UsageService
} from './api';

type LoadingStep = 'idle' | 'site' | 'auth' | 'data' | 'success' | 'logout';

const App: React.FC = () => {
  const auth = useAuth();
    const [isAuthCallback] = useState(() => window.location.search.includes("code=") || window.location.pathname.includes("signin-oidc"));

  // --- Data States ---
  const [user, setUser] = useState<User | null>(null);
  const [config, setConfig] = useState<AppConfig>({
    dialect: 'najdi', ttsEnabled: true, voice: 'Kore', speechSpeed: 1.0, theme: 'dark', 
    mode: 'chat', language: 'ar', hasSeenOnboarding: false, dataSourceMode: 'local'
  });
  const [loadingStep, setLoadingStep] = useState<LoadingStep>(isAuthCallback ? 'auth' : 'site');

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [flows, setFlows] = useState<IntegrationFlow[]>([]);
  const [agents, setAgents] = useState<AgentProfile[]>([]);
  const [services, setServices] = useState<ServiceDefinition[]>([]);
  const [integrations, setIntegrations] = useState<IntegrationApp[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]); 

  // --- UI States ---
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'profile' | 'app' | 'security'>('app');
  const [isPlansOpen, setIsPlansOpen] = useState(false);
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [isLiveActive, setIsLiveActive] = useState(false);
  const [toasts, setToasts] = useState<ToastType[]>([]);
  const [dialog, setDialog] = useState<any>({ isOpen: false });

  const t = TRANSLATIONS[config.language];
  const isAr = config.language === 'ar';

  // --- OIDC Auth Effect (Robust User Capture) ---
  useEffect(() => {
    if (!auth.isLoading && auth.userData) {
      const oidcProfile = auth.userData.profile;
      
      console.log("OIDC User Profile:", oidcProfile);

      // Determine Role from Claims
      let assignedRole: UserRole = 'user';
      const roleClaim = ((oidcProfile as any).role || (oidcProfile as any).roles || '').toString().toLowerCase();
      
      if (roleClaim.includes('superadmin')) assignedRole = 'superadmin';
      else if (roleClaim.includes('super')) assignedRole = 'super';
      else if (roleClaim.includes('admin')) assignedRole = 'admin';

      const mappedUser: User = {
        id: oidcProfile.sub || 'oidc_user',
        name: oidcProfile.name || oidcProfile.preferred_username || (oidcProfile.email ? oidcProfile.email.split('@')[0] : 'Lahja User'),
        email: oidcProfile.email || '',
        avatar: oidcProfile.picture || (oidcProfile as any).avatar || (oidcProfile as any).icon || (oidcProfile as any).image || (oidcProfile as any).photo,
        plan: (oidcProfile as any).plan || 'pro', 
        role: assignedRole
      };
      
      setUser(mappedUser);
      localStorage.setItem('lahja_token', auth.userData.access_token || '');
      localStorage.setItem('lahja_user', JSON.stringify(mappedUser));
    }
  }, [auth.userData, auth.isLoading]);

  // --- Initialization ---
  useEffect(() => {
    const initApp = async () => {
      try {
        if (auth.isLoading) return;
        const [u, c] = await Promise.all([
          UserService.getProfile(),
          ConfigService.getConfig()
        ]);
        if (!user && u) setUser(u);
        setConfig(c);
        if (u && !c.hasSeenOnboarding) setIsOnboardingOpen(true);
      } catch (e) {
        console.error("Init failed", e);
      }
    };
    initApp();
  }, [auth.isLoading]);

  // --- Data Fetching ---
  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      try {
        const fetchedPlans = await PlansService.getAll();
        setPlans(fetchedPlans);
        const [convs, fls, agts, svcs, ints] = await Promise.all([
          ChatService.getHistory(),
          FlowsService.getAll(),
          AgentsService.getAll(),
          ServicesService.getAll(),
          IntegrationsService.getAll()
        ]);
        setConversations(convs);
        setFlows(fls);
        setAgents(agts);
        setServices(svcs);
        setIntegrations(ints);
        const lastId = localStorage.getItem('lahja_current_id');
        if (lastId && convs.find(c => c.id === lastId)) setCurrentId(lastId);
      } catch (e) {
        console.error("Data fetch error", e);
        addToast('error', 'Failed to load application data');
      }
    };
    fetchData();
  }, [user, config.dataSourceMode]);

  // --- Theme & Language ---
  useEffect(() => {
    if (config.theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    document.documentElement.dir = config.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = config.language;
  }, [config.theme, config.language]);

  useEffect(() => {
    if (currentId) localStorage.setItem('lahja_current_id', currentId);
  }, [currentId]);

  // --- Handlers ---
  const addToast = (type: ToastType['type'], message: string) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, message }]);
  };

  const removeToast = (id: string) => setToasts(prev => prev.filter(t => t.id !== id));
  const closeDialog = () => setDialog({ isOpen: false });

  const handleUpdateConfig = async (newConfig: Partial<AppConfig>) => {
    setConfig(prev => ({ ...prev, ...newConfig }));
    await ConfigService.updateConfig(newConfig);
  };

  const handleUpdateUser = async (u: Partial<User>) => {
    if (!user) return;
    const updated = { ...user, ...u } as User;
    setUser(updated);
    localStorage.setItem('lahja_user', JSON.stringify(updated));
    await UserService.updateProfile(u);
  };

  const toggleTheme = () => handleUpdateConfig({ theme: config.theme === 'light' ? 'dark' : 'light' });
  
  const setAppMode = (mode: AppMode) => {
    handleUpdateConfig({ mode });
    if (mode === 'chat') setIsLiveActive(false);
  };

  const openSettingsAt = (tab: 'profile' | 'app' | 'security' = 'app') => {
    setSettingsTab(tab);
    setIsSettingsOpen(true);
  };

  const handleLogin = async (loggedUser: User) => {
    try {
      const u = await AuthService.login(loggedUser.email, loggedUser.name);
      setUser(u);
      addToast('success', config.language === 'ar' ? `مرحباً بك مجدداً يا ${u.name}` : `Welcome back, ${u.name}`);
    } catch (e) {
      addToast('error', 'Login Failed');
    }
  };

  const handleLogout = () => {
    setDialog({
      isOpen: true,
      type: 'warning',
      title: t.logoutTitle,
      message: t.logoutMsg,
      confirmText: t.confirm,
      onConfirm: async () => {
        if (auth && auth.userData) {
             await auth.signOutRedirect({ post_logout_redirect_uri: window.location.origin +"/"});
        } else {
            await AuthService.logout();
        }
        setUser(null);
        localStorage.removeItem('lahja_user');
        localStorage.removeItem('lahja_token');
        setConversations([]);
        setCurrentId(null);
        closeDialog();
      }
    });
  };

  const handleDeleteAccount = () => {
    setDialog({
      isOpen: true,
      type: 'danger',
      title: t.deleteAccountTitle,
      message: t.deleteAccountMsg,
      confirmText: t.confirm,
      onConfirm: async () => {
        await UserService.deleteAccount();
        if (auth && auth.userData) auth.signOut();
        setUser(null);
        window.location.reload();
      }
    });
  };

  const handleSendMessage = async (content: string, attachments?: Attachment[]) => {
    if (!config.apiKey && !process.env.API_KEY) {
      openSettingsAt('security');
      addToast('info', isAr ? 'يرجى تهيئة مفتاح الربط للمتابعة' : 'Please configure API Key to continue');
      return;
    }

    const permission = await UsageService.checkPermission('message');
    if (!permission.allowed) {
      setIsPlansOpen(true);
      addToast('error', permission.reason || (isAr ? 'لقد وصلت للحد الأقصى.' : 'Limit reached.'));
      setIsLiveActive(false);
      return;
    }
    UsageService.trackEvent('message');
    let activeId = currentId;
    let currentConv = conversations.find(c => c.id === activeId);
    if (!activeId || !currentConv) {
      const newConv: Conversation = {
        id: Date.now().toString(),
        title: content.substring(0, 30) || t.newChat,
        messages: [],
        updatedAt: Date.now(),
        dialect: config.dialect
      };
      setConversations(prev => [newConv, ...prev]);
      setCurrentId(newConv.id);
      activeId = newConv.id;
      currentConv = newConv;
      await ChatService.saveConversation(newConv);
    }
    const userMsg: Message = { id: Date.now().toString(), role: 'user', content, timestamp: Date.now(), attachments };
    const updatedConv = {
        ...currentConv, 
        messages: [...(currentConv.messages || []), userMsg],
        updatedAt: Date.now(),
        title: currentConv.messages.length === 0 ? content.substring(0, 25) : currentConv.title
    };
    setConversations(prev => prev.map(c => c.id === activeId ? updatedConv : c));
    setIsLoading(true);
    await ChatService.saveConversation(updatedConv);
    try {
      const history = updatedConv.messages.map(m => ({ role: m.role, content: m.content }));
      const responseText = await generateResponse(content, config.dialect, history, attachments, config.language, config.apiKey);
      const assistantMsg: Message = { id: (Date.now() + 1).toString(), role: 'assistant', content: responseText, timestamp: Date.now() };
      const finalConv = { ...updatedConv, messages: [...updatedConv.messages, assistantMsg], updatedAt: Date.now() };
      setConversations(prev => prev.map(c => c.id === activeId ? finalConv : c));
      await ChatService.saveConversation(finalConv);
      if (config.ttsEnabled && responseText) {
        const base64 = await generateAudio(responseText, config.voice, config.apiKey);
        if (base64) await audioService.playPCM(base64, config.speechSpeed);
      }
    } catch (err: any) {
      addToast('error', err.message || 'Connection Error');
      setIsLiveActive(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddFlow = async (flowData: Omit<IntegrationFlow, 'id'>) => {
      const permission = await UsageService.checkPermission('create_flow');
      if (!permission.allowed) { setIsPlansOpen(true); addToast('error', permission.reason || 'Upgrade required'); return; }
      UsageService.trackEvent('flow_execution');
      const newFlow = await FlowsService.create(flowData);
      setFlows(prev => [...prev, newFlow]);
      addToast('success', isAr ? 'تم إنشاء التدفق' : 'Flow Created');
  };

  const handleDeleteFlow = async (id: string) => {
      await FlowsService.delete(id);
      setFlows(prev => prev.filter(f => f.id !== id));
      addToast('info', isAr ? 'تم حذف التدفق' : 'Flow Deleted');
  };

  const handleToggleFlow = async (id: string) => {
      const flow = flows.find(f => f.id === id);
      if (flow) {
          const updated = { ...flow, active: !flow.active };
          setFlows(prev => prev.map(f => f.id === id ? updated : f));
          await FlowsService.save(updated);
      }
  };

  const handleDeleteChat = (id: string) => {
    setDialog({
      isOpen: true, type: 'danger', title: t.deleteChatTitle, message: t.deleteChatMsg, confirmText: t.confirm,
      onConfirm: async () => {
        await ChatService.deleteConversation(id);
        setConversations(prev => prev.filter(c => c.id !== id));
        if (currentId === id) setCurrentId(null);
        closeDialog();
        addToast('success', 'Deleted');
      }
    });
  };

  const handleClearAllChats = () => {
    setDialog({
      isOpen: true, type: 'danger', title: t.clearAllTitle, message: t.clearAllMsg, confirmText: t.confirm,
      onConfirm: async () => {
        await ChatService.clearAll();
        setConversations([]);
        setCurrentId(null);
        closeDialog();
        addToast('success', 'Cleared');
      }
    });
  };

  // --- Render Logic ---

  if (auth.isLoading) {
    return (
      <div className={`flex h-full w-full items-center justify-center ${config.theme === 'dark' ? 'bg-[#08080a]' : 'bg-slate-50'}`}>
        <div className="flex flex-col items-center gap-4">
           <div className="w-16 h-16 rounded-3xl bg-emerald-500 flex items-center justify-center shadow-[0_0_40px_rgba(16,185,129,0.3)] animate-pulse">
              <img src="logo.png" className="w-8 h-8 object-contain brightness-0 invert" alt="L" />
           </div>
           <p className="text-emerald-500 font-black text-xs uppercase tracking-[0.3em] animate-pulse">Authenticating</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <AuthTemplate 
        onLogin={handleLogin} 
        language={config.language} 
        theme={config.theme} 
        onToggleTheme={toggleTheme} 
        onSwitchLanguage={(lang) => handleUpdateConfig({ language: lang })}
        onLahjaLoginClick={() => auth.signIn()} 
      />
    );
  }

  return (
    <div className={`flex h-full w-full overflow-hidden ${config.theme === 'dark' ? 'dark' : ''} bg-slate-50 dark:bg-[#08080a] transition-colors duration-500`}>
      <Toast toasts={toasts} onRemove={removeToast} />
      <ModernDialog isOpen={dialog.isOpen} {...dialog} onCancel={closeDialog} />
      <CommandPalette isOpen={isCommandOpen} onClose={() => setIsCommandOpen(false)} language={config.language} services={services} onNavigate={setAppMode} onExecuteAction={(act, p) => {}} />
      <OnboardingTour isOpen={isOnboardingOpen} onClose={() => { setIsOnboardingOpen(false); handleUpdateConfig({ hasSeenOnboarding: true }); }} language={config.language} onNavigate={setAppMode} />

      {(config.mode === 'chat' || config.mode === 'live') && (
        <SidebarTemplate conversations={conversations} currentId={currentId} onSelect={setCurrentId} onNewChat={() => { setCurrentId(null); setAppMode('chat'); }} onDelete={handleDeleteChat} onClearAll={handleClearAllChats} onOpenSettings={() => openSettingsAt('app')} onOpenPlans={() => setIsPlansOpen(true)} onSwitchMode={setAppMode} theme={config.theme} toggleTheme={toggleTheme} isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} user={user} onLogout={handleLogout} language={config.language} onSwitchLanguage={(lang) => handleUpdateConfig({ language: lang })} />
      )}

      <main className="flex-1 flex flex-col min-w-0 bg-white dark:bg-[#0f0f13] relative transition-all duration-500 overflow-hidden">
        <AppHeader config={config} user={user} usageCount={0} messageLimit={15} onSidebarOpen={() => setIsSidebarOpen(true)} onNavOpen={() => setIsNavOpen(true)} onSettingsOpen={() => openSettingsAt('app')} onSwitchMode={setAppMode} onResetChat={() => { setCurrentId(null); setAppMode('chat'); }} onOpenCommand={() => setIsCommandOpen(true)} />
        <AppContent 
           config={config} 
           user={user} 
           usageCount={0} 
           conversations={conversations} 
           currentId={currentId} 
           flows={flows} 
           agents={agents} 
           services={services} 
           setServices={setServices} 
           plans={plans} 
           setPlans={setPlans} 
           integrations={integrations} 
           setIntegrations={setIntegrations} 
           isLoading={isLoading} 
           isLiveActive={isLiveActive} 
           setIsLiveActive={setIsLiveActive} 
           setAppMode={setAppMode} 
           setAgents={setAgents} 
           setFlows={setFlows} 
           onUpdateUser={handleUpdateUser} 
           onOpenPlans={() => setIsPlansOpen(true)} 
           onNavOpen={() => setIsNavOpen(true)} 
           onUpdateConfig={handleUpdateConfig} 
           onSendMessage={handleSendMessage} 
           onAddFlow={handleAddFlow} 
           onDeleteFlow={handleDeleteFlow} 
           onToggleFlow={handleToggleFlow} 
           onSelectService={(n) => { setAppMode('chat'); handleSendMessage(isAr ? `أريد استخدام خدمة ${n}` : `Use service ${n}`); }} 
           onAutomateService={(s) => { handleAddFlow({ name: `${s.title} Automation`, active: true, nodes: [], edges: [] }); setAppMode('flows'); }} 
           onExecuteCommand={(act, p) => {}} 
           onOpenSettingsAt={openSettingsAt}
        />
      </main>

      <NavigationRailTemplate currentMode={config.mode} onSwitchMode={setAppMode} language={config.language} isOpen={isNavOpen} onClose={() => setIsNavOpen(false)} user={user} />
      <SettingsTemplate 
        config={config} 
        user={user} 
        isOpen={isSettingsOpen} 
        initialTab={settingsTab}
        onClose={() => setIsSettingsOpen(false)} 
        onUpdate={handleUpdateConfig} 
        onUpdateUser={handleUpdateUser} 
        onLogout={handleLogout} 
        onDeleteAccount={handleDeleteAccount} 
        onRestartTour={() => { setIsSettingsOpen(false); setIsOnboardingOpen(true); }} 
      />
      <PlansTemplate isOpen={isPlansOpen} onClose={() => setIsPlansOpen(false)} onActivationSuccess={(p) => { handleUpdateUser({ plan: p }); setIsPlansOpen(false); addToast('success', isAr ? 'تمت الترقية بنجاح!' : 'Upgrade Successful!'); }} language={config.language} plans={plans} />
    </div>
  );
};

export default App;
